import { relative, resolve } from "node:path";
import { existsSync, readFileSync } from "node:fs";
import type { CaiConfig, ProjectModel } from "./types.js";
import { checkToolConfigs } from "./drift/checkers/tool-configs.js";
import { scanProjectModel } from "./scanner/manifest.js";
import { HOOK_MARKERS } from "./watch.js";

type DoctorStatus = "ok" | "warn" | "info";

export interface DoctorCheck {
  name: string;
  status: DoctorStatus;
  message: string;
  advice?: string;
}

export interface DoctorReport {
  projectRoot: string;
  scaffoldRoot: string;
  project: ProjectModel;
  checks: DoctorCheck[];
}

const statusIcon: Record<DoctorStatus, string> = {
  ok: "✔",
  warn: "⚠",
  info: "ℹ",
};

export function runDoctor(config: CaiConfig): DoctorReport {
  const project = scanProjectModel(config.projectRoot);
  const toolConfigIssues = checkToolConfigs(config.projectRoot);
  const relativeScaffoldRoot =
    config.scaffoldRoot === config.projectRoot
      ? "."
      : relative(config.projectRoot, config.scaffoldRoot);
  const checks: DoctorCheck[] = [
    {
      name: "scaffold",
      status: "ok",
      message: `Scaffold root detected at ${relativeScaffoldRoot}`,
    },
  ];

  if (project.rootManifest) {
    checks.push({
      name: "manifest",
      status: "ok",
      message: `Primary manifest: ${project.rootManifest.type}${project.rootManifest.name ? ` (${project.rootManifest.name})` : ""}`,
    });
  } else {
    checks.push({
      name: "manifest",
      status: "warn",
      message: "No supported root manifest found.",
      advice: "Add a package.json, Cargo.toml, or similar manifest to your project root.",
    });
  }

  if (toolConfigIssues.length > 0) {
    checks.push({
      name: "tool-configs",
      status: "warn",
      message: `${toolConfigIssues.length} tool config file${toolConfigIssues.length === 1 ? "" : "s"} out of sync`,
      advice: "Run cai fix to bring tool config files back in sync.",
    });
  } else {
    checks.push({
      name: "tool-configs",
      status: "ok",
      message: "Tool config files are in sync.",
    });
  }

  if (project.workspaces.length === 0) {
    checks.push({
      name: "workspaces",
      status: "info",
      message: "No package.json workspaces detected — single-package project.",
    });
  } else {
    const isolated = findIsolatedWorkspaces(project);
    checks.push({
      name: "workspaces",
      status: isolated.length > 0 ? "info" : "ok",
      message:
        `${project.workspaces.length} workspace${project.workspaces.length === 1 ? "" : "s"}, ` +
        `${project.workspaceDependencies.length} internal dependenc${project.workspaceDependencies.length === 1 ? "y" : "ies"}` +
        (isolated.length > 0 ? `, ${isolated.length} isolated (no internal deps)` : ""),
      advice: isolated.length > 0
        ? "Isolated workspaces are not necessarily a problem — verify they are intentionally standalone."
        : undefined,
    });
  }

  if (project.commands.length === 0) {
    checks.push({
      name: "commands",
      status: "warn",
      message: "No root or workspace scripts detected.",
      advice: "Add scripts to your package.json so cai can track and verify them.",
    });
  } else {
    checks.push({
      name: "commands",
      status: "ok",
      message: `${project.commands.length} command target${project.commands.length === 1 ? "" : "s"} found in manifests.`,
    });
  }

  // Hook status
  const hookPath = resolve(config.projectRoot, ".git", "hooks", "post-commit");
  if (!existsSync(hookPath)) {
    checks.push({
      name: "post-commit",
      status: "info",
      message: "No post-commit hook installed.",
      advice: "Run 'cai watch' to auto-check drift after every commit.",
    });
  } else {
    const hookContent = readFileSync(hookPath, "utf-8");
    if (hookContent.includes(HOOK_MARKERS.start)) {
      checks.push({
        name: "post-commit",
        status: "ok",
        message: "cai post-commit hook is installed and active.",
      });
    } else if (hookContent.includes(HOOK_MARKERS.legacy)) {
      checks.push({
        name: "post-commit",
        status: "warn",
        message: "Legacy cai hook detected — installed by an older version.",
        advice: "Run 'cai watch --uninstall && cai watch' to upgrade to the current hook format.",
      });
    } else {
      checks.push({
        name: "post-commit",
        status: "info",
        message: "A post-commit hook exists but was not installed by cai.",
        advice: "Run 'cai watch' to add cai drift checking to your existing hook.",
      });
    }
  }

  // PreCompact hook status
  const settingsPath = resolve(config.projectRoot, ".claude", "settings.json");
  if (existsSync(settingsPath)) {
    try {
      const settings = JSON.parse(readFileSync(settingsPath, "utf-8"));
      const hooks = settings.hooks as Record<string, unknown> | undefined;
      if (hooks?.PreCompact) {
        checks.push({
          name: "pre-compact",
          status: "ok",
          message: "PreCompact hook installed — Claude re-reads context after compaction.",
        });
      } else {
        checks.push({
          name: "pre-compact",
          status: "warn",
          message: "No PreCompact hook — context may be lost after compaction.",
          advice: "Run 'cai setup' to install the PreCompact hook, or add it manually to .claude/settings.json.",
        });
      }
    } catch {
      checks.push({
        name: "post-compact",
        status: "info",
        message: "Could not parse .claude/settings.json.",
      });
    }
  } else {
    checks.push({
      name: "post-compact",
      status: "info",
      message: "No .claude/settings.json found.",
      advice: "Run 'cai setup' to create Claude Code configuration with MCP and PreCompact hook.",
    });
  }

  return {
    projectRoot: config.projectRoot,
    scaffoldRoot: config.scaffoldRoot,
    project,
    checks,
  };
}

export function runDoctorNoScaffold(projectRoot: string): DoctorReport {
  const root = resolve(projectRoot);
  const project = scanProjectModel(root);
  const checks: DoctorCheck[] = [
    {
      name: "scaffold",
      status: "info",
      message: "No CAI scaffold required for this read-only diagnosis.",
      advice: "Run 'cai setup --dry-run' to preview setup, or 'cai setup --minimal' to create only .cai/.",
    },
  ];

  if (project.rootManifest) {
    checks.push({
      name: "manifest",
      status: "ok",
      message: `Primary manifest: ${project.rootManifest.type}${project.rootManifest.name ? ` (${project.rootManifest.name})` : ""}`,
    });
  } else {
    checks.push({
      name: "manifest",
      status: "warn",
      message: "No supported root manifest found.",
      advice: "CAI works best when it can inspect package.json, pyproject.toml, go.mod, Cargo.toml, pom.xml, or Gemfile.",
    });
  }

  if (project.workspaces.length === 0) {
    checks.push({
      name: "workspaces",
      status: "info",
      message: "No package.json workspaces detected — single-package project.",
    });
  } else {
    checks.push({
      name: "workspaces",
      status: "ok",
      message: `${project.workspaces.length} workspace${project.workspaces.length === 1 ? "" : "s"} detected.`,
    });
  }

  if (project.commands.length === 0) {
    checks.push({
      name: "commands",
      status: "info",
      message: "No root or workspace scripts detected.",
    });
  } else {
    checks.push({
      name: "commands",
      status: "ok",
      message: `${project.commands.length} command target${project.commands.length === 1 ? "" : "s"} found in manifests.`,
    });
  }

  return {
    projectRoot: root,
    scaffoldRoot: "(not installed)",
    project,
    checks,
  };
}

export function printDoctor(report: DoctorReport): void {
  const hasWarnings = report.checks.some((c) => c.status === "warn");

  console.log();
  console.log("cai doctor — scaffold diagnostics");
  console.log();
  console.log(`  Project:  ${report.projectRoot}`);
  console.log(`  Scaffold: ${report.scaffoldRoot}`);
  console.log();

  for (const check of report.checks) {
    const icon = statusIcon[check.status];
    console.log(`${icon} ${check.name.padEnd(14)} ${check.message}`);
    if (check.advice) {
      console.log(`  ℹ ${check.advice}`);
    }
  }

  if (report.project.workspaces.length > 0) {
    console.log();
    console.log("Workspace topology:");
    for (const workspace of report.project.workspaces) {
      const name = workspace.manifest.name ?? "(unnamed)";
      const outgoing = report.project.workspaceDependencies.filter((edge) => edge.from === workspace.path);
      const deps = outgoing.length > 0
        ? outgoing.map((edge) => edge.to).join(", ")
        : "no internal deps";
      console.log(`  · ${workspace.path} (${name}) → ${deps}`);
    }
  }

  console.log();
  if (hasWarnings) {
    console.log("  ℹ Run cai check for full drift analysis, or cai fix to repair safe issues.");
  } else {
    console.log("  ℹ Scaffold looks healthy. Run cai check for full drift analysis.");
  }
  console.log();
}

function findIsolatedWorkspaces(project: ProjectModel): string[] {
  const connected = new Set<string>();
  for (const edge of project.workspaceDependencies) {
    connected.add(edge.from);
    connected.add(edge.to);
  }
  return project.workspaces
    .map((workspace) => workspace.path)
    .filter((path) => !connected.has(path));
}
